import { atom, selector, selectorFamily } from 'recoil';
import { RawScenario } from '../types/rawScenario';
import { getQuestionScenario } from '../api/getQuestionScenario';
import { getQuestions, Questions } from "../api/getQuestions";
import {
  translateQuestionsToScenario,
  translateRawToDefaultForm,
  translateRawToScenarioItems,
} from '../utils/translator';
import { Scenario } from './scenarioState';
import { pageState } from './pageState';
import { Address, getAddress } from '../api/getAddress';
import { jsonArray, jsonValue } from '../types/jsonValue';

export const tokenState = selector<string>({
  key: 'tokenSelector',
  get: ({get}) => {
    const page = get(pageState);
    return 'token' in page ? Promise.resolve(page.token) : Promise.reject();
  }
});

export const getQuestionsQuery = selector<Questions>({
  key: 'getQuestionsQuery',
  get: async ({get}) => {
    const token = get(tokenState);
    if (token == null) {
      throw new Error('token not initialized');
    }
    return await getQuestions({token}).toPromise();
  }
});

export const questionIdState = atom<number | null>({
  key: 'questionId',
  default: selector({
    key: 'questionIdSelector',
    get: async ({get}) => {
      const page = get(pageState);
      if (page.location !== 'main') return null;
      const questions = get(getQuestionsQuery);

      if (questions.questions.length === 1) {
        return questions.questions[0].questionId;
      }

      return null;
    }
  })
});

export const getQuestionScenarioQuery = selector<RawScenario | null>({
  key: 'getQuestionScenarioQuery',
  get: async ({get}) => {
    const token = get(tokenState);
    if (token == null) return null;

    const questionId = get(questionIdState);
    if (questionId == null) return null;

    return await getQuestionScenario({token, questionId}).toPromise();
  }
});

export const scenarioQuery = selector<Scenario>({
  key: 'scenarioQuery',
  get: ({get}) => {
    const questions = get(getQuestionsQuery);
    const rawScenario = get(getQuestionScenarioQuery);

    const scenario = translateQuestionsToScenario(questions);
    const scenarioItems = rawScenario != null ? translateRawToScenarioItems(rawScenario) : [];

    return {
      ...scenario,
      items: [...scenario.items, ...scenarioItems]
    };
  }
});

export const scenarioDefaultFormQuery = selector<{ [id: string]: jsonValue | jsonArray }>({
  key: 'scenarioDefaultFormQuery',
  get: ({get}) => {
    const scenario = get(getQuestionScenarioQuery);
    if (scenario == null) return {};
    return translateRawToDefaultForm(scenario);
  }
});

export const previewScenarioQuery = selector<Scenario>({
  key: 'previewScenarioQuery',
  get: ({get}) => {
    const questions = get(getQuestionsQuery);
    const rawScenario = get(getQuestionScenarioQuery);

    const scenario = translateQuestionsToScenario(questions);
    const scenarioItems = rawScenario != null ? translateRawToScenarioItems(rawScenario, true) : [];

    return {
      ...scenario,
      items: scenarioItems
    };
  }
});

export const getAddressQuery = selectorFamily<Address | null, string | null>({
  key: 'getAddressQuery',
  get: postalCode => async ({get}) => {
    const token = get(tokenState);
    if (token == null || !postalCode?.match(/^\d{3}-\d{4}$/)) {
      return null;
    }
    return await getAddress({token, zipcode: postalCode}).toPromise();
  }
});
